import { CommonModule } from '@angular/common';
import { Component, computed, input } from '@angular/core';
import type { ChipThemePalette } from '@neogov/pattern-library/core';
import { NeoChipsModule } from '@neogov/pattern-library/neo-chips';

/**
 * Chip color enum - single source of truth for chip colors.
 */
export enum ChipColor {
  INFORMATIVE = 'informative',
  PRIMARY = 'primary',
  SUCCESS = 'success',
  WARNING = 'warning',
  DANGER = 'danger',
  INFO = 'info',
  DEFAULT = 'default',
  NEUTRAL = 'neutral',
}

/**
 * Chip size type - 'sm' | 'md'
 */
export type NeoChipSize = 'sm' | 'md';

/**
 * Chip size constants for easy reference.
 */
export const ChipSize = {
  SM: 'sm' as NeoChipSize,
  MD: 'md' as NeoChipSize,
} as const;

export type ChipColorValue = `${ChipColor}`;

// Map CM color names to neo-chip's ChipThemePalette
export const CHIP_COLOR_MAP: Record<ChipColor, ChipThemePalette> = {
  [ChipColor.INFORMATIVE]: 'informative',
  [ChipColor.PRIMARY]: 'neutral',
  [ChipColor.SUCCESS]: 'positive',
  [ChipColor.WARNING]: 'warning',
  [ChipColor.DANGER]: 'negative',
  [ChipColor.INFO]: 'informative',
  [ChipColor.DEFAULT]: 'neutral',
  [ChipColor.NEUTRAL]: 'neutral',
};

@Component({
  selector: 'cm-chip',
  standalone: true,
  imports: [CommonModule, NeoChipsModule],
  templateUrl: './chip.component.html',
  styleUrl: './chip.component.less',
})
export class ChipComponent {
  public readonly color = input<ChipColorValue>(ChipColor.DEFAULT);
  public readonly size = input<NeoChipSize>('sm');
  /** Custom background color (overrides color input) */
  public readonly backgroundColor = input<string | undefined>(undefined);
  /** Custom text color (only used with backgroundColor) */
  public readonly textColor = input<string>('#ffffff');

  public readonly neoColor = computed<ChipThemePalette>(() => CHIP_COLOR_MAP[this.color() as ChipColor]);
  public readonly useCustomColors = computed(() => !!this.backgroundColor());

  public readonly neoCssClass = computed(() => {
    const classes = [];

    if (this.color() === ChipColor.PRIMARY) {
      classes.push('chip-primary');
    }

    return classes.join(' ');
  });
}
